import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { CycleLifecycle } from '../types/planningLifecycle';
import { currentCycle, serviceScope, standardCycle, validCycle } from '../lib/planningCycles';
import { validServiceRange } from '../lib/servicePeriods';
export function useOptionState() {
  const [cycles, setCycles] = useState<CycleLifecycle[]>([]);
  const [preparationCycles, setPreparationCycles] = useState<CycleLifecycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const pending = useRef<Promise<void> | null>(null);
  const loaded = useRef(false);
  const generation = useRef(0);
  const load = useCallback((refresh = false) => {
    if (pending.current && !refresh) return pending.current;
    if (loaded.current && !refresh) return Promise.resolve();
    const version = ++generation.current;
    setLoading(true);
    setError(false);
    const request = (async () => {
      try {
        const result = await apiClient.getPlanningCycleOptions();
        if (
          result.error ||
          !Array.isArray(result.data?.cycles) ||
          (result.data.preparationCycles !== undefined &&
            !Array.isArray(result.data.preparationCycles))
        )
          throw new Error('Invalid cycle catalogue');
        if (version !== generation.current) return;
        setCycles(result.data.cycles);
        setPreparationCycles(result.data.preparationCycles ?? []);
        setRevision(value => value + 1);
        loaded.current = true;
      } catch {
        if (version === generation.current) {
          setError(true);
          loaded.current = false;
        }
      } finally {
        if (version === generation.current) {
          setLoading(false);
          pending.current = null;
        }
      }
    })();
    pending.current = request;
    return request;
  }, []);
  useEffect(
    () => () => {
      generation.current += 1;
      pending.current = null;
      loaded.current = false;
    },
    []
  );
  return { cycles, preparationCycles, loading, error, load, revision };
}
type PlanningSessionValue = ReturnType<typeof useOptionState> & {
  cycle: string | null;
  remember: (cycle: string) => void;
  blocked: boolean;
  setBlocked: (value: boolean) => void;
};
export const PlanningSessionContext = createContext<PlanningSessionValue | null>(null);
export const usePlanningSession = () => useContext(PlanningSessionContext);
export function usePlanningOptions() {
  const session = usePlanningSession();
  const local = useOptionState();
  const load = session?.load ?? local.load;
  useEffect(() => {
    void load();
  }, [load]);
  return {
    ...(session ?? local),
    retry: () => {
      void load(true);
    },
  };
}
export function usePlanningScope(kind: 'cycle' | 'service', override?: string) {
  const [params, setParams] = useSearchParams();
  const session = usePlanningSession();
  const explicit = params.get('scopeId') ?? override;
  const raw = explicit ?? session?.cycle ?? currentCycle();
  const canonical =
    standardCycle(raw) ??
    (kind === 'cycle' && validServiceRange(raw)
      ? standardCycle(`${raw.slice(0, 4)}-${Math.floor((Number(raw.slice(5, 7)) - 1) / 2) + 1}`)
      : null);
  const value = kind === 'service' ? serviceScope(canonical ?? raw) : (canonical ?? raw);
  const valid = kind === 'service' ? validServiceRange(value) : validCycle(value);
  const remember = session?.remember;
  useEffect(() => {
    if (canonical) remember?.(canonical);
  }, [canonical, remember]);
  const select = (next: string) => {
    if (validCycle(next)) remember?.(next);
    setParams({ scopeId: kind === 'service' ? serviceScope(next) : next });
  };
  return { scopeId: value, cycleId: canonical, valid, select };
}
export function usePlanningNavigationGuard(blocked: boolean) {
  const session = usePlanningSession();
  const setBlocked = session?.setBlocked;
  useEffect(() => {
    setBlocked?.(blocked);
    return () => setBlocked?.(false);
  }, [blocked, setBlocked]);
}
